// SkillExecuteModal — Onda G.1 plano v6 (Skills Cycle Complete).
//
// Modal "Disparar skill" — gerado a partir de skill.inputs_schema.
//
// inputs_schema shape: { fields: [{ name, type, required, description, enum_values? }, ...] }
//   - type 'string'   → input text
//   - type 'number'   → input number
//   - type 'boolean'  → checkbox
//   - type 'enum'     → select com enum_values
//   - type 'longtext' → textarea + opcao upload arquivo (paste OU upload — tu disse no plano)
//
// Sem inputs_schema definido: modal mostra textarea "JSON livre" como fallback.

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import { Play, AlertCircle } from 'lucide-react';
import { api } from '../lib/api';

export interface SkillInputsField {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'enum' | 'longtext';
  required?: boolean;
  description?: string;
  enum_values?: string[];
}

export interface SkillInputsSchema {
  fields?: SkillInputsField[];
}

export interface SkillExecuteModalProps {
  open: boolean;
  onClose: () => void;
  skill: {
    slug: string;
    name: string;
    version: string;
    inputs_schema?: SkillInputsSchema | Record<string, unknown> | null;
  };
  onDispatched?: (taskId: string) => void;
}

export function SkillExecuteModal({ open, onClose, skill, onDispatched }: SkillExecuteModalProps) {
  const schema = (skill.inputs_schema as SkillInputsSchema) ?? {};
  const fields = schema.fields ?? [];
  const hasSchema = fields.length > 0;

  const [values, setValues] = useState<Record<string, unknown>>({});
  const [rawJson, setRawJson] = useState('{}');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setField = (name: string, val: unknown) => setValues((v) => ({ ...v, [name]: val }));

  const onFileUpload = async (fieldName: string, file: File) => {
    const text = await file.text();
    setField(fieldName, text);
  };

  const handleDispatch = async () => {
    setLoading(true);
    setError(null);
    try {
      let args: Record<string, unknown>;
      if (hasSchema) {
        // valida required
        const missing = fields.filter((f) => f.required && !values[f.name]);
        if (missing.length > 0) {
          setError(`Campos obrigatorios faltando: ${missing.map((f) => f.name).join(', ')}`);
          setLoading(false);
          return;
        }
        args = values;
      } else {
        try {
          args = JSON.parse(rawJson || '{}');
        } catch (parseErr) {
          setError(`JSON invalido: ${parseErr instanceof Error ? parseErr.message : String(parseErr)}`);
          setLoading(false);
          return;
        }
      }

      // Dispatcha via /skills/:slug/execute (rota existente que cria task event_type='skill.standalone')
      const result = await api.executeSkill(skill.slug, args);
      onDispatched?.(result.task_id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Play className="h-5 w-5 text-green-600" />
            Disparar skill: {skill.name}
            <span className="text-xs font-normal text-muted-foreground">v{skill.version}</span>
          </DialogTitle>
          <DialogDescription>
            {hasSchema
              ? `${fields.length} campo${fields.length > 1 ? 's' : ''} definido${fields.length > 1 ? 's' : ''} no inputs_schema. Obrigatórios marcados com *.`
              : 'Esta skill nao tem inputs_schema declarado. Cole JSON livre abaixo ou deixe {} pra rodar sem args.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {hasSchema ? (
            fields.map((field) => (
              <div key={field.name}>
                <label className="block text-sm font-medium mb-1">
                  {field.name}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                {field.description && (
                  <p className="text-xs text-muted-foreground mb-2">{field.description}</p>
                )}
                {field.type === 'enum' && field.enum_values && (
                  <Select
                    value={String(values[field.name] ?? '')}
                    onChange={(v) => setField(field.name, v)}
                    options={[
                      { value: '', label: 'Selecione...' },
                      ...field.enum_values.map((v) => ({ value: v, label: v })),
                    ]}
                  />
                )}
                {field.type === 'string' && (
                  <Input
                    value={String(values[field.name] ?? '')}
                    onChange={(e) => setField(field.name, e.target.value)}
                  />
                )}
                {field.type === 'number' && (
                  <Input
                    type="number"
                    value={String(values[field.name] ?? '')}
                    onChange={(e) => setField(field.name, Number(e.target.value))}
                  />
                )}
                {field.type === 'boolean' && (
                  <Checkbox
                    checked={Boolean(values[field.name])}
                    onCheckedChange={(checked) => setField(field.name, checked)}
                  />
                )}
                {field.type === 'longtext' && (
                  <div className="space-y-2">
                    <Textarea
                      value={String(values[field.name] ?? '')}
                      onChange={(e) => setField(field.name, e.target.value)}
                      rows={6}
                      placeholder="Cole o conteudo aqui..."
                    />
                    <input
                      type="file"
                      accept=".txt,.md,.json,.csv"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void onFileUpload(field.name, f);
                      }}
                      className="text-xs"
                    />
                  </div>
                )}
              </div>
            ))
          ) : (
            <div>
              <label className="block text-sm font-medium mb-1">Args (JSON)</label>
              <Textarea
                value={rawJson}
                onChange={(e) => setRawJson(e.target.value)}
                rows={8}
                className="font-mono text-xs"
                placeholder='{"key": "value"}'
              />
            </div>
          )}

          {error && (
            <div className="rounded border border-red-300 bg-red-50 dark:bg-red-950/40 p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={loading}>Cancelar</Button>
          <Button variant="brand" onClick={handleDispatch} disabled={loading}>
            {loading ? 'Disparando...' : 'Disparar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
